import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { PrintQrModal } from "@/components/PrintQrModal";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { TagsSection } from "@/components/TagsSection";
import { adminStationsApi, adminUsersApi, stagesApi } from "@/lib/api";
import { displayName } from "@/lib/format";
import type { StationReadDetailed, UUID } from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function StationDetail() {
  const { id } = useParams<{ id: string }>();
  const stationId = id ? Number(id) : NaN;
  if (!Number.isFinite(stationId)) return <ErrorState error={new Error("Bad id")} />;
  return (
    <RequirePermission codes="station.manage">
      <Inner id={stationId} />
    </RequirePermission>
  );
}

function Inner({ id }: { id: number }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["admin", "stations", id],
    queryFn: () => adminStationsApi.get(id),
  });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const station = q.data!;
  return (
    <>
      <PageHeader
        eyebrow={`admin · station · ${station.code}`}
        title={station.name}
        description={t("stationDetail.headerDescription", { active: station.is_active ? t("common.yes") : t("common.no"), count: station.operator_count })}
        actions={
          <div className="flex gap-2">
            <PrintStationQrButton id={station.id} code={station.code} />
            <Link to="/admin/stations" className="btn">{t("stationDetail.allStations")}</Link>
          </div>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <IdentitySection station={station} />
        <OperatorsSection station={station} />
        <TagsSection
          targetType="station"
          targetId={String(station.id)}
          hint={t("stationDetail.tagsHint")}
        />
      </div>
    </>
  );
}

function IdentitySection({ station }: { station: StationReadDetailed }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState(station.code);
  const [name, setName] = useState(station.name);
  const [stageId, setStageId] = useState<number | null>(station.stage_id);
  const [isActive, setIsActive] = useState(station.is_active);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setCode(station.code);
    setName(station.name);
    setStageId(station.stage_id);
    setIsActive(station.is_active);
  }, [station.id, station.code, station.name, station.stage_id, station.is_active]);

  const stages = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
    staleTime: 60_000,
  });

  const m = useMutation({
    mutationFn: () =>
      adminStationsApi.update(station.id, {
        code,
        name,
        stage_id: stageId,
        is_active: isActive,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "stations", station.id] }),
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  const toggleActive = useMutation({
    mutationFn: () =>
      station.is_active
        ? adminStationsApi.deactivate(station.id)
        : adminStationsApi.activate(station.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "stations", station.id] }),
  });

  return (
    <section className="card p-5 space-y-4">
      <h3 className="text-lg">{t("stationDetail.identity")}</h3>
      <Field label={t("stationDetail.codeLabel")}>
        <input className={`${INPUT_CLS} font-mono`} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
      </Field>
      <Field label={t("stationDetail.nameLabel")}>
        <input className={INPUT_CLS} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("stationDetail.stageLabel")}>
        <select
          className={`${INPUT_CLS} font-mono`}
          value={stageId ?? ""}
          onChange={(e) => setStageId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t("stationDetail.stageNotSet")}</option>
          {stages.data?.items.map((s) => (
            <option key={s.id} value={s.id}>{s.code} · {displayName(s)}</option>
          ))}
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        {t("stationDetail.activeCheckbox")}
      </label>
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <div className="flex items-center gap-2">
        <button
          className="btn btn-primary"
          onClick={() => { setErr(null); m.mutate(); }}
          disabled={m.isPending}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
        <button
          className={`btn ${station.is_active ? "btn-danger" : ""}`}
          onClick={() => {
            if (station.is_active && !window.confirm(t("stationDetail.confirmDeactivate"))) return;
            toggleActive.mutate();
          }}
          disabled={toggleActive.isPending}
        >
          {station.is_active ? t("stationDetail.deactivate") : t("stationDetail.activate")}
        </button>
      </div>
    </section>
  );
}

function OperatorsSection({ station }: { station: StationReadDetailed }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const usersQ = useQuery({
    queryKey: ["admin", "users", "all", "for-station-detail"],
    queryFn: () => adminUsersApi.list({ page_size: 200 }),
    staleTime: 60_000,
  });

  // Currently assigned operator IDs — extract by querying every user's stations?
  // Cheap path: fetch all operators, then ask backend per user (slow), or use the
  // station detail's `operator_count`. Better: GET /admin/stations/{id}/operators —
  // эндпоинта пока нет, поэтому считаем через GET /admin/users/{id}/stations.
  const assignedQ = useQuery({
    queryKey: ["admin", "stations", station.id, "operators"],
    enabled: !!usersQ.data,
    queryFn: async () => {
      const ids = new Set<UUID>();
      const users = usersQ.data?.items ?? [];
      // Parallel fetch
      const lists = await Promise.all(
        users.map((u) =>
          adminUsersApi.listStations(u.id).then((sts) => ({ id: u.id, hit: sts.some((s) => s.id === station.id) })),
        ),
      );
      for (const r of lists) if (r.hit) ids.add(r.id);
      return ids;
    },
  });

  const [selected, setSelected] = useState<Set<UUID>>(new Set());
  useEffect(() => {
    if (assignedQ.data) setSelected(new Set(assignedQ.data));
  }, [assignedQ.data]);

  const save = useMutation({
    mutationFn: () =>
      adminStationsApi.setOperators(station.id, { operator_ids: [...selected] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "stations", station.id] });
      qc.invalidateQueries({ queryKey: ["admin", "stations", station.id, "operators"] });
    },
  });

  function toggle(id: UUID, on: boolean) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  if (usersQ.isLoading || assignedQ.isLoading) return <section className="card p-5"><LoadingState /></section>;

  const operators = (usersQ.data?.items ?? []).filter((u) => u.is_active && u.role !== "SYSTEM" && u.role !== "ERP_AGENT");
  const original = assignedQ.data ?? new Set<UUID>();
  const dirty = selected.size !== original.size || [...selected].some((id) => !original.has(id));

  return (
    <section className="card p-5 space-y-4">
      <h3 className="text-lg">{t("stationDetail.operatorsTitle")}</h3>
      <p className="text-xs text-text-muted">
        {t("stationDetail.operatorsHint")}
      </p>
      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {operators.length === 0 && <p className="text-text-muted text-sm">{t("stationDetail.noOperators")}</p>}
        {operators.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-surface-hover">
            <input
              type="checkbox"
              checked={selected.has(u.id)}
              onChange={(e) => toggle(u.id, e.target.checked)}
            />
            <span className="flex-1 min-w-0 truncate">
              {u.name}
              {u.login && <span className="font-mono text-text-muted text-xs ml-1">({u.login})</span>}
            </span>
            <span className="pill pill-accent text-xs">{u.role}</span>
          </label>
        ))}
      </div>
      {save.isError && <ErrorState error={save.error} />}
      <button
        className="btn btn-primary"
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t("common.saving") : t("stationDetail.saveBindings")}
      </button>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function PrintStationQrButton({ id, code }: { id: number; code: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        title={t("stations.printQrTitle", { code })}
      >
        {t("stationDetail.printQr")}
      </button>
      {open && (
        <PrintQrModal
          templateKind="station-qr"
          subjectLabel={code}
          onClose={() => setOpen(false)}
          onPrint={(target) => adminStationsApi.printQr(id, target)}
        />
      )}
    </>
  );
}
